import { Injectable, NgZone, DestroyRef, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subject, fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { retry, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface VersionInfo {
  version: string;
  buildTime: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private destroyRef = inject(DestroyRef);
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);

  private currentVersion: string = environment.appVersion;
  private currentBuildTime: string | null = null;
  private checkIntervalMs = 2 * 60 * 1000; // Check every 2 minutes
  private minCheckIntervalMs = 60 * 1000; // Minimum 60 seconds between checks (cooldown)
  private lastCheckTime: number = 0; // Track last check timestamp
  private updateAvailable$ = new Subject<VersionInfo>();
  private isTabVisible = true;
  private visibilityCheckEnabled = true;
  private isCheckingVersion = false;

  constructor() {
    // this.initializeVisibilityTracking();
  }

  /**
   * Initialize Page Visibility API tracking to pause polling when tab is hidden
   */
  private initializeVisibilityTracking(): void {
    if (typeof document === 'undefined') return;

    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.isTabVisible = !document.hidden;
          console.log('[VersionCheck] Tab visibility changed:', this.isTabVisible ? 'visible' : 'hidden');

          // If tab becomes visible, check version with cooldown protection
          if (this.isTabVisible) {
            this.ngZone.run(() => this.checkVersionWithCooldown());
          }
        });
    });
  }

  /**
   * Start checking for version updates
   * @returns Observable that emits when new version is available
   */
  public startVersionCheck() {
    console.log('[VersionCheck] Starting version monitoring. Current version:', this.currentVersion);

    // Check immediately on start (only if tab is visible)
    if (this.isTabVisible) {
      this.checkVersion();
    } else {
      console.log('[VersionCheck] Tab hidden on startup, deferring initial check');
    }

    // Then check every 2 minutes (but only when tab is visible)
    interval(this.checkIntervalMs)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => !this.visibilityCheckEnabled || this.isTabVisible) // Skip polling if tab is hidden
      )
      .subscribe(() => {
        console.log('[VersionCheck] Periodic check triggered');
        this.checkVersion();
      });

    return this.updateAvailable$.asObservable();
  }

  /**
   * Check version with cooldown protection
   * Prevents excessive API calls when user switches tabs frequently
   */
  private checkVersionWithCooldown(): void {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastCheckTime;

    // Skip check if within cooldown period
    if (this.lastCheckTime > 0 && timeSinceLastCheck < this.minCheckIntervalMs) {
      const remainingTime = Math.ceil((this.minCheckIntervalMs - timeSinceLastCheck) / 1000);
      console.log(`[VersionCheck] Cooldown active. Next check available in ${remainingTime}s`);
      return;
    }

    // Cooldown expired or first check - proceed
    this.checkVersion();
  }

  /**
   * Check if new version is available
   * Compares both version number AND buildTime to detect same-version hotfixes
   */
  private checkVersion(): void {
    // Prevent concurrent HTTP requests
    if (this.isCheckingVersion) {
      console.log('[VersionCheck] Check already in progress, skipping duplicate request');
      return;
    }

    this.isCheckingVersion = true;
    this.lastCheckTime = Date.now(); // Update last check timestamp
    const timestamp = new Date().getTime();

    this.http.get<VersionInfo>(`/assets/version.json?t=${timestamp}`, {
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    })
      .pipe(
        retry(2) // Retry up to 2 times on network failure
      )
      .subscribe({
        next: (versionInfo) => {
          console.log('[VersionCheck] Current:', this.currentVersion,
            'Build:', this.currentBuildTime || 'unknown',
            '| Remote:', versionInfo.version,
            'Build:', versionInfo.buildTime);

          // Check for version change
          const versionChanged = versionInfo.version !== this.currentVersion;

          // Check for buildTime change (only if buildTime was previously initialized)
          const isFirstCheck = this.currentBuildTime === null;
          const buildTimeChanged = !isFirstCheck && versionInfo.buildTime !== this.currentBuildTime;

          // Initialize buildTime on first check
          if (isFirstCheck) {
            this.currentBuildTime = versionInfo.buildTime;
            console.log('[VersionCheck] Initialized buildTime:', this.currentBuildTime);
          }

          // Trigger update if version OR buildTime changed (handles same-version hotfixes)
          if (versionChanged || buildTimeChanged) {
            const changeType = versionChanged ? 'Version update' : 'Same-version hotfix';
            console.log(`[VersionCheck] ${changeType} detected:`,
              versionInfo.version,
              'Build:', versionInfo.buildTime);
            this.updateAvailable$.next(versionInfo);
          }

          this.isCheckingVersion = false;
        },
        error: (error) => {
          console.error('[VersionCheck] Failed to check version after retries:', error);
          this.isCheckingVersion = false;
        }
      });
  }

  /**
   * Get current app version
   */
  public getCurrentVersion(): string {
    return this.currentVersion;
  }
}
